import * as THREE from 'three';
import type { Terrain } from '../world/Terrain';
import type { TeamId, Vec3 } from '@use/shared';
import {
  TANK_HP, TANK_MOVE_SPEED, TANK_TURN_SPEED,
  TANK_MAX_SLOPE_DEG, SLOPE_SLIDE_FORCE,
  getSlopeAngle, getSlopeSpeedMultiplier, getDownhillDirection,
} from '@use/shared';

export class Tank {
  public group: THREE.Group;
  public hull: THREE.Mesh;
  public turretGroup: THREE.Group;
  public turret: THREE.Mesh;
  public barrel: THREE.Mesh;
  public barrelTip: THREE.Vector3; // world-space fire point

  public playerId: string;
  public teamId: TeamId;
  public hp: number = TANK_HP;
  public alive: boolean = true;

  public turretYaw: number = 0;
  public barrelPitch: number = 0.3;

  private nameTag: THREE.Sprite | null = null;
  private hpBarBg: THREE.Sprite | null = null;
  private hpBarFill: THREE.Sprite | null = null;

  constructor(
    scene: THREE.Scene,
    playerId: string,
    teamId: TeamId,
    position: Vec3,
    playerName: string = 'Player'
  ) {
    this.playerId = playerId;
    this.teamId = teamId;
    this.group = new THREE.Group();
    this.group.position.set(position.x, position.y, position.z);

    const teamColor = teamId === 'red' ? 0xcc3333 : 0x3366cc;
    const teamDark = teamId === 'red' ? 0x882222 : 0x224488;

    // Hull — box shape
    const hullGeo = new THREE.BoxGeometry(3, 1.2, 4.5);
    const hullMat = new THREE.MeshLambertMaterial({ color: teamColor, flatShading: true });
    this.hull = new THREE.Mesh(hullGeo, hullMat);
    this.hull.position.y = 0.6;
    this.hull.castShadow = true;
    this.group.add(this.hull);

    // Track covers (sides)
    const trackGeo = new THREE.BoxGeometry(0.4, 0.9, 4.8);
    const trackMat = new THREE.MeshLambertMaterial({ color: 0x333333, flatShading: true });
    const leftTrack = new THREE.Mesh(trackGeo, trackMat);
    leftTrack.position.set(-1.7, 0.45, 0);
    leftTrack.castShadow = true;
    this.group.add(leftTrack);

    const rightTrack = new THREE.Mesh(trackGeo, trackMat);
    rightTrack.position.set(1.7, 0.45, 0);
    rightTrack.castShadow = true;
    this.group.add(rightTrack);

    // Turret group (rotates on Y)
    this.turretGroup = new THREE.Group();
    this.turretGroup.position.y = 1.2;
    this.group.add(this.turretGroup);

    // Turret body
    const turretGeo = new THREE.BoxGeometry(2, 0.8, 2.5);
    const turretMat = new THREE.MeshLambertMaterial({ color: teamDark, flatShading: true });
    this.turret = new THREE.Mesh(turretGeo, turretMat);
    this.turret.position.y = 0.4;
    this.turret.castShadow = true;
    this.turretGroup.add(this.turret);

    // Barrel (rotates on X via turret group)
    const barrelGeo = new THREE.CylinderGeometry(0.15, 0.15, 4, 8);
    barrelGeo.translate(0, 2, 0); // pivot at base
    barrelGeo.rotateX(Math.PI / 2); // point forward along Z
    const barrelMat = new THREE.MeshLambertMaterial({ color: 0x555555, flatShading: true });
    this.barrel = new THREE.Mesh(barrelGeo, barrelMat);
    this.barrel.position.set(0, 0.8, 0);
    this.barrel.castShadow = true;
    this.turretGroup.add(this.barrel);

    this.barrelTip = new THREE.Vector3();

    // Name tag
    this.createNameTag(playerName, teamColor);

    scene.add(this.group);
  }

  private createNameTag(name: string, color: number): void {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d')!;

    ctx.font = 'bold 28px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillStyle = '#' + color.toString(16).padStart(6, '0');
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 3;
    ctx.strokeText(name, 128, 38);
    ctx.fillText(name, 128, 38);

    const tex = new THREE.CanvasTexture(canvas);
    const mat = new THREE.SpriteMaterial({ map: tex, transparent: true });
    this.nameTag = new THREE.Sprite(mat);
    this.nameTag.scale.set(6, 1.5, 1);
    this.nameTag.position.y = 4;
    this.group.add(this.nameTag);
  }

  /**
   * Update tank position/rotation to align with terrain.
   */
  public updateTerrainAlignment(terrain: Terrain): void {
    const pos = this.group.position;
    const h = terrain.getHeightAt(pos.x, pos.z);
    pos.y = h;

    // Align to terrain normal
    const normal = terrain.getNormalAt(pos.x, pos.z);
    const up = new THREE.Vector3(0, 1, 0);
    const n = new THREE.Vector3(normal.x, normal.y, normal.z);
    const q = new THREE.Quaternion().setFromUnitVectors(up, n);

    // Preserve the tank's Y rotation (heading)
    const heading = new THREE.Quaternion().setFromAxisAngle(
      new THREE.Vector3(0, 1, 0),
      this.group.rotation.y
    );
    this.group.quaternion.copy(q).multiply(heading);
  }

  /**
   * Apply turret yaw and barrel pitch.
   */
  public updateTurret(): void {
    this.turretGroup.rotation.y = this.turretYaw;
    this.barrel.rotation.x = -this.barrelPitch; // negative because barrel points along Z
  }

  /**
   * Get the world-space position of the barrel tip (fire point).
   */
  public getBarrelTip(): THREE.Vector3 {
    // The barrel mesh has geometry translated so the tip is at local (0, 0, 4)
    const tipLocal = new THREE.Vector3(0, 0, 4);
    this.barrel.localToWorld(tipLocal);
    this.barrelTip.copy(tipLocal);
    return this.barrelTip.clone();
  }

  /**
   * Get the world-space fire direction based on turret + barrel angle.
   */
  public getFireDirection(): THREE.Vector3 {
    const dir = new THREE.Vector3(0, 0, 1);
    const worldQuat = new THREE.Quaternion();
    this.barrel.getWorldQuaternion(worldQuat);
    dir.applyQuaternion(worldQuat);
    return dir.normalize();
  }

  /**
   * Move the tank forward/backward and turn, with slope-aware speed modulation.
   */
  public move(forward: number, turn: number, dt: number, terrain?: Terrain): void {
    if (!this.alive) return;

    // Turn
    this.group.rotation.y += turn * TANK_TURN_SPEED * dt;

    // Get slope at current position
    let speedMult = 1;
    if (terrain) {
      const pos = this.group.position;
      const slopeAngle = getSlopeAngle(terrain.heightmap, pos.x, pos.z);
      speedMult = getSlopeSpeedMultiplier(slopeAngle, TANK_MAX_SLOPE_DEG);

      // Slide downhill on steep slopes
      if (slopeAngle > TANK_MAX_SLOPE_DEG * 0.7) {
        const downhill = getDownhillDirection(terrain.heightmap, pos.x, pos.z);
        const slideFactor = Math.min(1, (slopeAngle - TANK_MAX_SLOPE_DEG * 0.7) / (TANK_MAX_SLOPE_DEG * 0.3));
        pos.x += downhill.x * SLOPE_SLIDE_FORCE * slideFactor * dt;
        pos.z += downhill.z * SLOPE_SLIDE_FORCE * slideFactor * dt;
      }
    }

    // Move forward in the direction the tank is facing
    const dir = new THREE.Vector3(0, 0, 1)
      .applyAxisAngle(new THREE.Vector3(0, 1, 0), this.group.rotation.y);
    this.group.position.addScaledVector(dir, forward * TANK_MOVE_SPEED * speedMult * dt);
  }

  /**
   * Set position from network state.
   */
  public setPosition(pos: Vec3): void {
    this.group.position.set(pos.x, pos.y, pos.z);
  }

  /**
   * Update HP and visual state.
   */
  public setHP(hp: number): void {
    this.hp = hp;
    if (hp <= 0) {
      this.alive = false;
      this.showDestroyed();
    }
  }

  private showDestroyed(): void {
    // Turn dark gray + flatten slightly to indicate wreck
    (this.hull.material as THREE.MeshLambertMaterial).color.set(0x333333);
    (this.turret.material as THREE.MeshLambertMaterial).color.set(0x222222);
    this.turretGroup.rotation.z = 0.2; // knocked askew
  }

  public setVisible(visible: boolean): void {
    this.group.visible = visible;
  }

  public dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
  }
}
